import type { TFunction } from "i18next";
import { getDefaultWalletDateRange } from "@/lib/walletDateRange";
import { parseMoneyInputToCents } from "@/lib/money";
import { walletMovementSearchText } from "@/lib/walletMovementLabels";
import { matchesTextQuery } from "@/lib/textSearch";
import type { WalletTransaction } from "@/types";

export type WalletTransactionFilterState = {
  startDate: string;
  endDate: string;
  selectedActivity: string;
  descriptionFilter: string;
  minAmount: string;
  maxAmount: string;
};

export function getDefaultWalletTransactionFilters(): WalletTransactionFilterState {
  const { startDate, endDate } = getDefaultWalletDateRange();
  return {
    startDate,
    endDate,
    selectedActivity: "",
    descriptionFilter: "",
    minAmount: "",
    maxAmount: "",
  };
}

export function countNonDateWalletFilters(
  applied: WalletTransactionFilterState,
): number {
  const defaults = getDefaultWalletTransactionFilters();
  let count = 0;
  if (applied.selectedActivity !== defaults.selectedActivity) count++;
  if (applied.descriptionFilter !== defaults.descriptionFilter) count++;
  if (applied.minAmount !== defaults.minAmount) count++;
  if (applied.maxAmount !== defaults.maxAmount) count++;
  return count;
}

export function buildWalletTransactionFilterParams(
  applied: WalletTransactionFilterState,
): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};
  if (applied.startDate) {
    // `YYYY-MM-DD` alone is UTC midnight; append T00:00:00 for local midnight.
    params.start_date = new Date(`${applied.startDate}T00:00:00`).toISOString();
  }
  if (applied.endDate) {
    const end = new Date(`${applied.endDate}T00:00:00`);
    end.setHours(23, 59, 59, 999);
    params.end_date = end.toISOString();
  }
  if (applied.selectedActivity) {
    params.activity_id = applied.selectedActivity;
  }
  return params;
}

export function filterWalletTransactionsClientSide(
  items: WalletTransaction[],
  applied: Pick<
    WalletTransactionFilterState,
    "descriptionFilter" | "minAmount" | "maxAmount"
  >,
  t: TFunction,
): WalletTransaction[] {
  return items.filter((tx) => {
    if (applied.descriptionFilter) {
      const fields = [
        walletMovementSearchText(tx, t),
        tx.activity_name,
        tx.service_name,
        tx.customer_name,
      ];
      if (!matchesTextQuery(applied.descriptionFilter, fields, { trim: false })) {
        return false;
      }
    }

    const minCents = applied.minAmount
      ? parseMoneyInputToCents(applied.minAmount)
      : null;
    const maxCents = applied.maxAmount
      ? parseMoneyInputToCents(applied.maxAmount)
      : null;
    if (minCents != null && tx.amount < minCents) {
      return false;
    }
    if (maxCents != null && tx.amount > maxCents) {
      return false;
    }

    return true;
  });
}
